import { Chip, Container, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import adminApi from "../../../api/adminApi";
import type { ObjectClassDefinitionInfo } from "../../../api/types/objectTypes";
import useClientContext from "../../../context/useClientContext";
import { pageRoutes } from "../../../routes";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import { checklistsPageTabs } from "./checklistsPageTabs";

interface ChecklistTypeRow {
  objectType: string;
  objectName: string;
  checklistsCount: number;
  taskTemplatesCount: number;
  updateAt?: string;
}

const getRowId = (row: ChecklistTypeRow) => row.objectType;

const NameCell = ({ row }: { row: ChecklistTypeRow }) => {
  const { clientCode } = useClientContext();

  const objectPath = `/${clientCode}/${pageRoutes.settings}/${pageRoutes.dataModel}/${pageRoutes.orgObjects}`;
  const backButtonPath = `/${clientCode}/${pageRoutes.workflows}/${pageRoutes.checklists}?tab=${checklistsPageTabs.checklistTypes}`;

  return (
    <GridTextHoverLink
      to={`${objectPath}/${row.objectType}/${pageRoutes.checklists}`}
      state={{ backButtonPath }}
      text={row.objectName}
    />
  );
};

const columnDefinitions: GridColDef<ChecklistTypeRow>[] = [
  {
    field: "objectName",
    headerName: "Name",
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }) => <NameCell row={row} />,
  },
  {
    field: "checklistsCount",
    headerName: "Checklists",
    align: "center",
    headerAlign: "center",
    flex: 0.5,
    minWidth: 140,
    renderCell: ({ row }) =>
      row.checklistsCount === 0 ? (
        <Chip label="Not configured" variant="outlined" />
      ) : (
        <Typography>{row.checklistsCount}</Typography>
      ),
  },
  {
    field: "taskTemplatesCount",
    headerName: "Task Templates",
    type: "number",
    align: "center",
    headerAlign: "center",
    flex: 0.5,
    minWidth: 150,
  },
  {
    field: "updateAt",
    headerName: "Modified at",
    flex: 0.75,
    minWidth: 180,
    valueFormatter: (value: string | undefined) => (value ? convertISODateTime(value) : "-"),
  },
];

const toChecklistTypeRow = (definition: ObjectClassDefinitionInfo): ChecklistTypeRow => ({
  objectType: definition.objectType,
  objectName: definition.objectName,
  checklistsCount: definition.checklists.length,
  taskTemplatesCount: new Set(definition.checklists.flatMap((checklist) => checklist.taskTemplateIds)).size,
  updateAt: definition.updateAt,
});

const ChecklistTypesTab = () => {
  const getChecklistDefinitions = useCallback(
    () => adminApi.searchOrganizationObjectDefinitions({ supportedFeatures: ["Checklists"] }),
    []
  );

  const [checklistDefinitions, fetchError, { isFetching }] = useFetch(getChecklistDefinitions);

  const rows = useMemo(() => (checklistDefinitions ?? []).map(toChecklistTypeRow), [checklistDefinitions]);

  if (fetchError) {
    logError(fetchError, "[ChecklistTypesTab] searchOrganizationObjectDefinitions");
    return <DataLoadingFailed title="Loading checklist types failed" />;
  }

  return (
    <>
      <GeneralPageHeader title="Checklist Types" />
      <Container maxWidth="xl" sx={{ flex: 1, overflow: "hidden", py: 2.5 }}>
        <DataGrid<ChecklistTypeRow>
          columns={columnDefinitions}
          rows={rows}
          getRowId={getRowId}
          loading={isFetching}
          noRowsText="No checklist types"
          disableColumnReorder
          disableColumnSorting
        />
      </Container>
    </>
  );
};

export default ChecklistTypesTab;
